/**
 * Autosave and resume for <wc-brief> (plan §3.3; K1's check W-E reads it).
 * Loaded by the element beside its core, and kept apart so the first-load
 * script stays under theme check's 60 KB.
 *
 * One brief per product per market, in localStorage (core.autosaveKey), never
 * the email address, and not offered back after 30 days. Restoring is never
 * automatic: the shopper is asked, and "Start again" deletes the save.
 */

export function save(el) {
  /** @type {Record<string, string | string[] | boolean>} */
  const fields = {};
  for (const node of el.form.querySelectorAll('[name^="wc-"]')) {
    const input = /** @type {HTMLInputElement} */ (node);
    if (input.name === "wc-email") continue; // never stored on the device
    if (input.type === "radio") {
      if (input.checked) fields[input.name] = input.value;
    } else if (input.type === "checkbox") {
      fields[input.name + (input.dataset.wcExtra ? `:${input.dataset.wcExtra}` : "")] = input.checked;
    } else if (input.name === "wc-subject") {
      (fields["wc-subject"] = /** @type {string[]} */ (fields["wc-subject"] || [])).push(input.value);
    } else {
      fields[input.name] = input.value;
    }
  }
  const payload = {
    v: 1,
    savedAt: Date.now(),
    step: el.step,
    options: el.options,
    briefId: el.briefId,
    fields,
  };
  try {
    localStorage.setItem(el.saveKey, JSON.stringify(payload));
  } catch {
    /* private mode or a full disk: autosave is a convenience */
  }
}

export function readSave(el) {
  try {
    const raw = localStorage.getItem(el.saveKey);
    const saved = raw && JSON.parse(raw);
    if (!saved || saved.v !== 1 || Date.now() - saved.savedAt > el.core.AUTOSAVE_TTL_MS) return null;
    return saved;
  } catch {
    return null;
  }
}

export function clearSave(el) {
  try {
    localStorage.removeItem(el.saveKey);
  } catch {
    /* nothing to clear */
  }
}

export function offerResume(el) {
  fromLink(el);
  const saved = readSave(el);
  const prompt = /** @type {HTMLElement | null} */ (el.querySelector("[data-wc-resume]"));
  if (!saved || !prompt) return;
  const people = Number(saved.fields["wc-people"]) || 0;
  const pets = Number(saved.fields["wc-pets"]) || 0;
  const names = (saved.fields["wc-subject"] || []).filter((n) => String(n).trim()).slice(0, 2);
  const who = names.length ? names.join(el.data.labels.and) : "";
  const text = /** @type {HTMLElement | null} */ (prompt.querySelector("[data-wc-resume-text]"));
  if (text) text.textContent = who ? el.data.labels.resumeOf.replace("%who%", who) : el.data.labels.resume;
  prompt.hidden = false;
  el.pendingResume = saved;
  const start = /** @type {HTMLElement | null} */ (el.querySelector("[data-wc-start]"));
  if (start) start.textContent = el.data.labels.startContinue;
}

export function resume(el, yes) {
  const prompt = /** @type {HTMLElement | null} */ (el.querySelector("[data-wc-resume]"));
  if (prompt) prompt.hidden = true;
  const saved = el.pendingResume;
  el.pendingResume = null;
  if (!yes || !saved) {
    clearSave(el);
    // "Start again" means step 1, not the step a reload's hash left open.
    if (!yes && el.step && el.step !== "who") el.go("who");
    return;
  }
  apply(el, saved);
  el.go(el.core.STEPS.includes(saved.step) ? el.clampStep(saved.step) : "who");
}

/**
 * A coded return link (I-58): `#<step>;<code>`, from "Email me my brief". The
 * code is read once, so a reload, a share or Back never replays it. A brief
 * saved on THIS device wins: it is newer than any email, and it is offered
 * back as usual. Otherwise the choices go in exactly as a resume puts them,
 * and route() then clamps the step to the first one still needing an answer
 * (names and photos never travel, so that is usually "who" or "photos").
 *
 * snippets/wc-head-early.liquid has usually taken the code out of the address
 * already, before content_for_header's analytics could read location.href
 * (the live proof on dev 9352684 found it in nine request bodies), and left
 * it in sessionStorage under this page's path. If storage refused it there,
 * the code is still in the hash, and it is read and stripped here instead.
 */
function fromLink(el) {
  let code;
  try {
    const key = `wc-brief-link:${location.pathname}`;
    code = sessionStorage.getItem(key) ?? undefined;
    sessionStorage.removeItem(key);
  } catch {
    /* storage refused: the head script left the code in the hash */
  }
  const [step, inHash] = location.hash.slice(1).replace(/%3B/gi, ";").split(";");
  if (inHash !== undefined) {
    code ??= inHash;
    history.replaceState(history.state, "", step ? `#${step}` : `${location.pathname}${location.search}`);
  }
  if (code === undefined) return;
  if (readSave(el)) return;
  const f = el.form;
  const inputs = (/** @type {string} */ name) =>
    /** @type {NodeListOf<HTMLInputElement>} */ (f.querySelectorAll(`input[name="${name}"]`));
  const c = el.core.decodeChoices(code, {
    recipients: inputs("wc-recipient").length,
    occasions: inputs("wc-occasion").length,
    artists: (el.data.artists || []).map((/** @type {{ handle: string }} */ a) => a.handle),
    extras: [...f.querySelectorAll("input[data-wc-extra]")].map((node) => ({
      key: /** @type {HTMLInputElement} */ (node).dataset.wcExtra || "",
      memorialOk: /** @type {HTMLInputElement} */ (node).dataset.memorialOk === "true",
    })),
    memorialProduct: Boolean(el.data.memorialProduct),
  });
  if (!c) return;
  /** @type {Record<string, string | boolean>} */
  const fields = {};
  if (c.people != null) fields["wc-people"] = String(c.people);
  if (c.pets != null) fields["wc-pets"] = String(c.pets);
  if (c.memorial) fields["wc-memorial"] = true;
  if (c.recipient != null) fields["wc-recipient"] = inputs("wc-recipient")[c.recipient].value;
  if (c.occasion != null) fields["wc-occasion"] = inputs("wc-occasion")[c.occasion].value;
  if (c.artist) fields["wc-artist"] = c.artist;
  if (c.angelWings) fields["wc-angel-wings"] = true;
  for (const key of Object.keys(c.extras || {})) fields[`wc-extra:${key}`] = true;
  if (c.payNow) fields["wc-plan"] = el.core.PAY_NOW;
  if (c.photosLater) fields["wc-photos"] = "later";
  apply(el, { fields });
}

/** Put a saved (or linked) brief's answers back into the form. */
function apply(el, saved) {
  el.restoring = true;
  const f = el.form;
  // A restored photos choice is the shopper's, never the brief's fallback (A34).
  if ("wc-photos" in saved.fields) el.photosAutoLater = false;
  for (const [name, value] of Object.entries(saved.fields)) {
    if (name === "wc-subject") continue;
    const [field, extra] = name.split(":");
    if (extra) {
      const box = /** @type {HTMLInputElement | null} */ (
        f.querySelector(`input[data-wc-extra="${CSS.escape(extra)}"]`)
      );
      if (box) box.checked = Boolean(value);
      continue;
    }
    for (const node of f.querySelectorAll(`[name="${CSS.escape(field)}"]`)) {
      const input = /** @type {HTMLInputElement} */ (node);
      if (input.type === "radio") input.checked = input.value === value;
      else if (input.type === "checkbox") input.checked = Boolean(value);
      else input.value = String(value);
    }
  }
  el.renderSubjects();
  [...f.querySelectorAll('input[name="wc-subject"]')].forEach((input, i) => {
    /** @type {HTMLInputElement} */ (input).value = saved.fields["wc-subject"]?.[i] ?? "";
  });
  el.briefId = saved.briefId || el.briefId;
  // The variant goes through Horizon like any other choice, so its price,
  // media and ?variant= follow. A saved choice this market no longer sells
  // (Tier 1 range, 12 Oct) moves to the nearest it does (core.settleOptions).
  // An explicit ?variant= wins over the saved choice (K9's style gallery links
  // to the style the shopper liked; Dani's idea, Luke's approval): its style,
  // size and finish stay, and the rest of the saved brief comes back. On a
  // plain reload Horizon has kept ?variant= on the brief's own choice, so the
  // two agree. A variant this market does not sell never counts as explicit.
  const linked = new URLSearchParams(location.search).get("variant");
  const explicit = el.data.variants.some((v) => String(v.id) === linked && el.core.purchasable(v));
  if (!explicit && Array.isArray(saved.options) && el.core.variantFor(el.data, saved.options)) {
    el.options = el.core.settleOptions(el.data, saved.options.map(String));
    el.syncVariantId?.();
    el.syncOptionRadios();
    el.tickPicker(el.options);
  }
  el.restoring = false;
  // W-E reads this attribute: the saved brief was put back (K1's check).
  el.toggleAttribute("data-restored", true);
  el.renderAll();
}
